import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { adminDayLabelSx, adminTimeFieldSx, adminToLabelSx } from "../shared/styles";

dayjs.extend(customParseFormat);

const TIME_FORMAT = "hh:mm A";

function parseDisplayTime(value) {
  if (!value || typeof value !== "string") return null;
  const parsed = dayjs(value.trim(), [TIME_FORMAT, "h:mm A", "HH:mm", "H:mm"], true);
  return parsed.isValid() ? parsed : null;
}

function formatDisplayTime(value) {
  if (!value || !value.isValid?.()) return "";
  return value.format(TIME_FORMAT);
}

function WorkingHoursTimePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const dayjsValue = useMemo(() => parseDisplayTime(value), [value]);

  return (
    <TimePicker
      ampm
      minutesStep={5}
      format={TIME_FORMAT}
      value={dayjsValue}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={(next) => {
        if (!next || !next.isValid()) return;
        onChange(formatDisplayTime(next));
      }}
      slotProps={{
        textField: {
          size: "small",
          onClick: () => setOpen(true),
          sx: {
            ...adminTimeFieldSx,
            width: 120,
            "& .MuiOutlinedInput-input": {
              ...adminTimeFieldSx["& .MuiOutlinedInput-input"],
              cursor: "pointer",
            },
          },
          inputProps: {
            readOnly: true,
            style: { textAlign: "center", cursor: "pointer" },
          },
        },
        openPickerButton: {
          sx: { display: "none" },
        },
        popper: {
          sx: {
            "& .MuiPaper-root": {
              borderRadius: "8px",
              border: "1px solid #D5DCE1",
              boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
            },
            // Meridiem column: keep both AM/PM visible. Put the selected period
            // on top and the other directly below. Drop MUI's :after spacer so
            // nothing scrolls out of view.
            "& .MuiMultiSectionDigitalClockSection-root:last-of-type": {
              display: "flex",
              flexDirection: "column",
              overflow: "hidden !important",
              "&::after": {
                display: "none",
              },
              '& [role="option"]': {
                order: 2,
              },
              '& [role="option"][aria-selected="true"]': {
                order: 1,
              },
            },
          },
        },
      }}
    />
  );
}

function WorkingHoursRow({ day, start, end, onStartChange, onEndChange }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        minHeight: "40px",
      }}
    >
      <Typography component="span" sx={adminDayLabelSx}>
        {day}
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,
          }}
        >
          <WorkingHoursTimePicker value={start} onChange={onStartChange} />
          <Typography component="span" sx={adminToLabelSx}>
            to
          </Typography>
          <WorkingHoursTimePicker value={end} onChange={onEndChange} />
        </Box>
      </LocalizationProvider>
    </Box>
  );
}

export default WorkingHoursRow;
